/**
 * Kontora BFF – Tour-Definitionen
 *
 * Jede Tour besteht aus mehreren Schritten. Ein Schritt referenziert ein
 * DOM-Element via CSS-Selector (`target`) und zeigt einen Tooltip daneben.
 *
 * Tour-IDs sind global eindeutig und werden im Onboarding-State persistiert.
 *
 * Pattern für `target`:
 *   - CSS-Selector: '#sidebar-buchhaltung', '[data-tour="context-tabs"]'
 *   - Wenn Element nicht gefunden wird, wird der Schritt übersprungen.
 *
 * Sicherheits-Hinweis:
 *   - `target` wird NUR in der BFF-Umgebung ausgeführt.
 *   - Wir nutzen keine externen CSS-Selector-Sprachen.
 *   - Selektoren sind hartcodiert und werden vom Entwickler gepflegt.
 */

import type { NavigationContext } from '@/src/lib/navigation';

export type TourPosition = 'top' | 'bottom' | 'left' | 'right' | 'center';

export interface TourStep {
  id: string;
  target: string;
  title: string;
  content: string;
  position?: TourPosition;
  /** Optional: Aktion, die der User ausführen soll, bevor weiter geht. */
  action?: 'click' | 'wait';
}

export interface Tour {
  id: string;
  title: string;
  description: string;
  context?: NavigationContext;
  steps: TourStep[];
}

export const TOURS: readonly Tour[] = [
  /* =====================================================================
   * Übersicht – Startseite & Quick-Access
   * ===================================================================== */
  {
    id: 'tour-overview',
    title: 'Willkommen in der Übersicht',
    description: 'Schneller Überblick über deinen Mandanten.',
    context: 'overview',
    steps: [
      {
        id: 'welcome',
        target: '[data-tour="app-shell"]',
        title: 'Willkommen bei Kontora',
        content:
          'Hier siehst du alles Wichtige auf einen Blick. Wir zeigen dir kurz die wichtigsten Bereiche.',
        position: 'center',
      },
      {
        id: 'finanzuebersicht',
        target: '[data-tour="finanzuebersicht"]',
        title: 'Finanzübersicht',
        content:
          'Deine Einnahmen, Ausgaben und offenen Posten – live aus der Buchhaltung.',
        position: 'bottom',
      },
      {
        id: 'aufgaben',
        target: '[data-tour="kritische-aufgaben"]',
        title: 'Kritische Aufgaben',
        content: 'Alle Aufgaben, die deine Aufmerksamkeit brauchen – sammelt sich hier automatisch.',
        position: 'bottom',
      },
      {
        id: 'benachrichtigungen',
        target: '[data-tour="notification-bell"]',
        title: 'Benachrichtigungen',
        content:
          'Hier landen System-Events, Mahnungen und Hinweise deines Steuerberaters.',
        position: 'bottom',
      },
    ],
  },

  /* =====================================================================
   * Operativ – Verkauf, Einkauf, Dokumente
   * ===================================================================== */
  {
    id: 'tour-operations',
    title: 'Operativer Bereich',
    description: 'Verkauf, Einkauf und Belege – die tägliche Arbeit.',
    context: 'operations',
    steps: [
      {
        id: 'sidebar-ops',
        target: '[data-tour="sidebar"]',
        title: 'Operativ-Bereich',
        content:
          'Hier findest du alle Bereiche für die tägliche Arbeit: Angebote, Rechnungen, Eingangsbelege, Dokumente.',
        position: 'right',
      },
      {
        id: 'kontext-tabs',
        target: '[data-tour="context-tabs"]',
        title: 'Kontext wechseln',
        content:
          'Oben kannst du jederzeit zwischen den Kontexten wechseln. Wir zeigen dir nur, was du in deinem aktuellen Kontext brauchst.',
        position: 'bottom',
      },
    ],
  },

  /* =====================================================================
   * Buchhaltung – Workflow-orientiert
   * ===================================================================== */
  {
    id: 'tour-accounting',
    title: 'Buchhaltungs-Workflow',
    description: 'Erfassen → Abgleichen → Buchen → Steuern → Auswerten → Abschließen.',
    context: 'accounting',
    steps: [
      {
        id: 'workflow-gruppen',
        target: '[data-tour="sidebar"]',
        title: 'Workflow-Gruppen',
        content:
          'Die Buchhaltung ist in sechs Schritte gegliedert. Du arbeitest dich typischerweise von oben nach unten durch.',
        position: 'right',
      },
      {
        id: 'erfassen',
        target: '[data-tour="group-erfassen"]',
        title: '1. Erfassen',
        content:
          'Sammle Belege, Bankimporte und Kassenbuch-Einträge ein, bevor du buchst.',
        position: 'right',
      },
      {
        id: 'abgleichen',
        target: '[data-tour="group-abgleichen"]',
        title: '2. Abgleichen',
        content:
          'Ordne Bankumsätze zu, stimme offene Posten ab und löse Mahnungen aus.',
        position: 'right',
      },
      {
        id: 'buchen',
        target: '[data-tour="group-buchen"]',
        title: '3. Buchen',
        content: 'Übernimm Buchungsvorschläge, prüfe das Journal und pflege deine Konten.',
        position: 'right',
      },
      {
        id: 'steuern',
        target: '[data-tour="group-steuern"]',
        title: '4. Steuern',
        content: 'Prüfe Tax Treatments, Rechnungshinweise und die UStVA-Vorschau.',
        position: 'right',
      },
      {
        id: 'auswerten',
        target: '[data-tour="group-auswerten"]',
        title: '5. Auswerten',
        content:
          'EUER, Cashflow, OPOS, Profitability – Reports für deine Auswertungen.',
        position: 'right',
      },
      {
        id: 'abschliessen',
        target: '[data-tour="group-abschliessen"]',
        title: '6. Abschließen',
        content:
          'Monatsabschluss, Perioden und (in Kürze) Jahresabschluss – mit Checklisten und Snapshots.',
        position: 'right',
      },
    ],
  },

  /* =====================================================================
   * Steuerberater – Zusammenarbeit mit Kanzlei
   * ===================================================================== */
  {
    id: 'tour-advisor',
    title: 'Steuerberater-Bereich',
    description: 'So arbeitest du mit deiner Kanzlei zusammen.',
    context: 'advisor',
    steps: [
      {
        id: 'kanzlei',
        target: '[data-tour="sidebar"]',
        title: 'Kanzlei-Zusammenarbeit',
        content:
          'Hier verwaltest du den Zugriff deiner Kanzlei, Kommentare, Aufgaben und Reviews.',
        position: 'right',
      },
      {
        id: 'aufgaben-liste',
        target: '[data-tour="group-arbeitslisten"]',
        title: 'Arbeitslisten',
        content:
          'Deine persönlichen Aufgaben und kritische Tickets – sammelt sich automatisch.',
        position: 'right',
      },
    ],
  },

  /* =====================================================================
   * Mandant – Stammdaten, Benutzer, Sicherheit
   * ===================================================================== */
  {
    id: 'tour-tenant',
    title: 'Mandanten-Verwaltung',
    description: 'Stammdaten, Benutzer, Sicherheit und Konfiguration.',
    context: 'tenant',
    steps: [
      {
        id: 'stammdaten',
        target: '[data-tour="group-stammdaten"]',
        title: 'Stammdaten',
        content:
          'Mandant, Partner, Produkte, Business Units, Projekte – alle Stammdaten an einem Ort.',
        position: 'right',
      },
      {
        id: 'benutzer',
        target: '[data-tour="group-benutzer-rechte"]',
        title: 'Benutzer & Rechte',
        content:
          'Verwalte Benutzer, Rollen und Sicherheitseinstellungen. Wer darf was?',
        position: 'right',
      },
      {
        id: 'kommunikation',
        target: '[data-tour="group-kommunikation"]',
        title: 'Kommunikation',
        content:
          'E-Mail-Vorlagen, Benachrichtigungen und das Design deiner Plattform.',
        position: 'right',
      },
    ],
  },

  /* =====================================================================
   * System – Sysadmin
   * ===================================================================== */
  {
    id: 'tour-system',
    title: 'System-Administration',
    description: 'Jobs, Scheduler, Logs und Exporte.',
    context: 'system',
    steps: [
      {
        id: 'power-mode',
        target: '[data-tour="power-mode-toggle"]',
        title: 'Power-Mode',
        content:
          'Mit dem Power-Mode siehst du ALLE Bereiche – auch die, für die du keine Permission hast. Nützlich für Diagnose.',
        position: 'bottom',
      },
      {
        id: 'betrieb',
        target: '[data-tour="group-betrieb"]',
        title: 'Betrieb',
        content:
          'Jobs, Scheduler, Exporte, Migration – alles rund um den Plattform-Betrieb.',
        position: 'right',
      },
      {
        id: 'beobachtbarkeit',
        target: '[data-tour="group-beobachtbarkeit"]',
        title: 'Beobachtbarkeit',
        content:
          'System-Health und Error-Logs – für Troubleshooting und Monitoring.',
        position: 'right',
      },
    ],
  },
];

export const TOUR_IDS = TOURS.map((t) => t.id);

export function findTour(id: string): Tour | undefined {
  return TOURS.find((t) => t.id === id);
}

export function toursForContext(context: NavigationContext): Tour[] {
  return TOURS.filter((t) => !t.context || t.context === context);
}